import { generateOneLinkUrl } from '@cian/appsflyer-onelink';
import { type IFullscreenBannerProps } from '@cian/mobile-banner-component';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MOBILE_BANNER_TITLE } from './constants';
import { trackClickOnBanner, trackOnCloseBanner, trackShowBanner } from './tracking';
import { useIsFullscreenBannerSkipped } from './useIsFullscreenBannerSkipped';
import { setFullscreenBannerByCookie } from '../../actions/cookies';
import { EMobileDeviceType } from '../../types/mobileDeviceType';
import { TThunkDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';

export function useMobileAppFullscreenBannerProps(): IFullscreenBannerProps | null {
  const dispatch = useDispatch<TThunkDispatch>();
  const { config, custom } = useApplicationContext();

  const shoudlSkipBanner = useIsFullscreenBannerSkipped();

  const [url, setUrl] = useState<string | null>(config.get<string>('mobileBanner.browserUrl') || null);

  useEffect(() => {
    setUrl(generateOneLinkUrl(url, 'mobile_site', MOBILE_BANNER_TITLE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBannerClick = useCallback(() => {
    dispatch(setFullscreenBannerByCookie());
    trackClickOnBanner(custom.mobileDeviceType);
  }, [dispatch, custom.mobileDeviceType]);

  const handleCloseBanner = useCallback(() => {
    dispatch(setFullscreenBannerByCookie());
    trackOnCloseBanner(custom.mobileDeviceType);
  }, [dispatch, custom.mobileDeviceType]);

  const handleShowBanner = useCallback(() => {
    trackShowBanner(custom.mobileDeviceType);
  }, [custom.mobileDeviceType]);

  return useMemo(() => {
    if (shoudlSkipBanner || !url || custom.mobileDeviceType === EMobileDeviceType.Unknown) {
      return null;
    }

    return {
      url,
      caShowBanner: handleShowBanner,
      caClickOnBanner: handleBannerClick,
      caCloseBanner: handleCloseBanner,
    };
  }, [shoudlSkipBanner, url, custom.mobileDeviceType, handleBannerClick, handleCloseBanner, handleShowBanner]);
}
